import React, { useEffect, useState, useMemo } from "react";
import {
  Col,
  Input,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Row,
} from "reactstrap";
import CustomInput from "Components/Custom/CustomInput";
import CustomButton from "Components/Custom/CustomButton";
import { useAppDispatch, useAppSelector } from "Components/Hooks/hooks";
import { useDebounce } from "Components/Hooks/useDebounce";
import {
  addItem,
  editItem,
  getCategories,
  getSubCategories,
} from "slices/thunks";
import { AddItemArgs, EditItemArgs, ItemData } from "types/item.api";
import { useFormik } from "formik";
import * as Yup from "yup";
import { QuoteDetailsPayload } from "types/quote.api";
import { InvoiceDetailsPayload } from "types/invoice.api";
import { PresetQuotePayload } from "types/preset.api";
import {
  VALIDATION_LIMITS,
  VALIDATION_MESSAGES,
} from "Components/constants/validation";

interface ManageItemModalProps {
  showModal: boolean;
  toggle: () => void;
  isEdit: boolean;
  onAddCategoryClick?: () => void;
  onAddSubCategoryClick?: () => void;
  selectedItem: ItemData | null;
  hideCategoryActions?: boolean;
  quoteDetails: QuoteDetailsPayload | null;
  invoiceDetails: InvoiceDetailsPayload | null;
  presetQuoteDetails?: PresetQuotePayload | null;
}

const ManageItemModal: React.FC<ManageItemModalProps> = ({
  showModal,
  toggle,
  isEdit,
  onAddCategoryClick,
  onAddSubCategoryClick,
  selectedItem,
  hideCategoryActions = false,
  quoteDetails,
  invoiceDetails,
  presetQuoteDetails,
}) => {
  const configData = useAppSelector((state) => state.config.configData);
  console.log("selectedItem", selectedItem);
  const [searchCategory, setSearchCategory] = useState("");
  const [searchSubCategory, setSearchSubCategory] = useState("");
  const debounceSearchCategory = useDebounce(searchCategory, 500);
  const debounceSearchSubCategory = useDebounce(searchSubCategory, 500);
  const dispatch = useAppDispatch();

  const { categories, isLoadingCategory } = useAppSelector(
    (stat) => stat.category,
  );
  const { isLoadingSubCategory, subcategories } = useAppSelector(
    (stat) => stat.subcategory,
  );

  // Memoize validation schema to prevent recreation
  const validationSchema = useMemo(
    () =>
      Yup.object({
        name: Yup.string()
          .trim()
          .required(VALIDATION_MESSAGES.REQUIRED("item name"))
          .max(
            VALIDATION_LIMITS.ITEM_NAME.MAX,
            VALIDATION_MESSAGES.MAX_LENGTH(
              "Item name",
              VALIDATION_LIMITS.ITEM_NAME.MAX,
            ),
          ),
        category_name: Yup.string().trim().required("Please select a category"),
        subcategory_name: Yup.string().when("category_id", {
          is: (val: number) => val && val > 0,
          then: (schema) => schema,
          otherwise: (schema) => schema,
        }),
        unit: Yup.string().trim().required("Please select a unit"),
        price: Yup.number()
          .positive("Price must be greater than 0")
          .required(VALIDATION_MESSAGES.REQUIRED("price"))
          .max(
            VALIDATION_LIMITS.COST.MAX,
            VALIDATION_MESSAGES.MAX_LENGTH("Price", VALIDATION_LIMITS.COST.MAX),
          ),
        cost: Yup.number()
          .min(0, "Unit cost must be 0 or greater")
          .required(VALIDATION_MESSAGES.REQUIRED("unit cost"))
          .max(
            VALIDATION_LIMITS.COST.MAX,
            VALIDATION_MESSAGES.MAX_LENGTH(
              "Unit cost",
              VALIDATION_LIMITS.COST.MAX,
            ),
          ),
        quantity: Yup.number()
          .min(1, "Quantity must be at least 1")
          .required(VALIDATION_MESSAGES.REQUIRED("quantity"))
          .max(
            VALIDATION_LIMITS.COST.MAX,
            VALIDATION_MESSAGES.MAX_LENGTH(
              "Unit cost",
              VALIDATION_LIMITS.COST.MAX,
            ),
          ),
      }),
    [],
  );

  const validation = useFormik<ItemData>({
    enableReinitialize: true,
    initialValues: {
      id: selectedItem?.id || 0,
      type: selectedItem?.type || "",
      name: selectedItem?.name || "",
      unit: selectedItem?.unit || "",
      quantity: selectedItem?.quantity || 1,
      price: selectedItem?.price || "",
      cost: selectedItem?.cost || "",
      is_added: selectedItem?.is_added,
      category_id: selectedItem?.category_id || 0,
      category_name: selectedItem?.category_name || "",
      subcategory_id: selectedItem?.subcategory_id || null,
      subcategory_name: selectedItem?.subcategory_name || "",
    } as ItemData,
    validationSchema,
    onSubmit: (values: ItemData) => {
      console.log("Form Values:", values);
      if (isEdit) {
        const updatedItem: EditItemArgs = {
          id: values.id,
          name: values.name?.trim(),
          unit: values.unit?.trim(),
          price: values.price,
          cost: values.cost,
          category_id: values.category_id,
          subcategory_id: values.subcategory_id || null,
          type: values.type,
          quantity: values.quantity,
          quote_id: quoteDetails?.id,
          invoice_id: invoiceDetails?.id,
          quote_template_id: presetQuoteDetails?.id,
        };
        dispatch(editItem(updatedItem));
      } else {
        const newItem: AddItemArgs = {
          name: values.name?.trim(),
          unit: values.unit?.trim(),
          price: values.price,
          cost: values.cost,
          category_id: values.category_id,
          subcategory_id: values.subcategory_id || null,
          quote_id: quoteDetails?.id,
          quantity: values.quantity,
          invoice_id: invoiceDetails?.id,
          quote_template_id: presetQuoteDetails?.id,
        };

        console.log(newItem);

        dispatch(addItem(newItem));
        console.log("Adding new item:", newItem);
      }
      validation.resetForm();
      toggle();
    },
  });

  // Fetch categories only when search changes or modal opens
  useEffect(() => {
    if (showModal) {
      dispatch(getCategories({ search: searchCategory }));
    }
  }, [debounceSearchCategory, dispatch]);

  // Fetch subcategories only when search changes or modal opens
  useEffect(() => {
    if (showModal && validation.values.category_id) {
      dispatch(
        getSubCategories({
          search: searchSubCategory,
          category_id: validation.values.category_id,
        }),
      );
    }
  }, [
    debounceSearchSubCategory,
    showModal,
    dispatch,
    validation.values.category_id,
  ]);

  // Memoize dropdown options to prevent recreation
  const categoryOptions = useMemo(
    () => categories.map((cat) => ({ label: cat.name, value: cat.id })),
    [categories],
  );

  const subcategoryOptions = useMemo(
    () =>
      subcategories
        .filter((cat) => cat.category.id === validation.values.category_id)
        .map((cat) => ({
          label: cat.name,
          value: cat.id,
        })),
    [subcategories, validation.values.category_id],
  );

  return (
    <Modal
      id="showModal"
      isOpen={showModal}
      toggle={() => {
        validation.resetForm();
        toggle();
      }}
      centered
      contentClassName="br-7"
    >
      <ModalHeader
        className="bg-greyF5 p-3 bg-light"
        toggle={() => {
          validation.resetForm();
          toggle();
        }}
      >
        {!!isEdit ? "Edit Item" : "Add Item"}
      </ModalHeader>

      <form
        onSubmit={(e) => {
          e.preventDefault();
          validation.handleSubmit();
          return false;
        }}
      >
        <ModalBody>
          <Input type="hidden" id="id-field" />

          <Row>
            <Col md={hideCategoryActions ? 6 : 12}>
              <div className="d-flex gap-2 align-items-center">
                <div className="flex-fill field-inline">
                  <CustomInput
                    label="Category"
                    type="select"
                    name="category_name"
                    placeholder="Search or select a category"
                    onChange={(e) => {
                      const selectedCategory = categories.find(
                        (cat) => cat.id === Number(e.target.value),
                      );
                      validation.setFieldValue("category_id", e.target.value);
                      validation.setFieldValue(
                        "category_name",
                        selectedCategory?.name || "",
                      );
                      // Reset subcategory when category changes
                      validation.setFieldValue("subcategory_id", null);
                      validation.setFieldValue("subcategory_name", "");
                      setSearchSubCategory("");
                    }}
                    onBlur={validation.handleBlur}
                    value={validation.values.category_id}
                    dropdownOptions={categoryOptions}
                    error={validation.errors.category_name}
                    touched={validation.touched.category_name}
                    onSearch={setSearchCategory}
                    isLoadingOptions={isLoadingCategory}
                    isDisabled={isEdit}
                  />
                </div>
                {hideCategoryActions ? null : (
                  <div className="mt-3">
                    <CustomButton
                      newClassName="bg-white border-1 border-border align-items-center justify-content-center gap-1 "
                      text="Category"
                      textColor="text-black2D"
                      leftIcon={
                        <i
                          className="ri-add-line align-bottom text-black2D"
                          style={{ fontSize: 17 }}
                        />
                      }
                      onClick={onAddCategoryClick}
                    />
                  </div>
                )}
              </div>
            </Col>
            <div
              className={` ${validation?.errors?.category_name ? "m-2" : ""}`}
            />

            <Col md={hideCategoryActions ? 6 : 12}>
              <div className="d-flex gap-2 align-items-center">
                <div className="flex-fill field-inline">
                  <CustomInput
                    label="Subcategory"
                    type="select"
                    name="subcategory_name"
                    // placeholder={
                    //   !validation.values.category_id ||
                    //   validation.values.category_id === 0
                    //     ? "Select a category first"
                    //     : "Search or select a subcategory"
                    // }
                    placeholder="Search or select a subcategory"
                    onChange={(e) => {
                      const selectedSubCategory = subcategories.find(
                        (cat) => cat.id === Number(e.target.value),
                      );
                      validation.setFieldValue(
                        "subcategory_id",
                        e.target.value,
                      );
                      validation.setFieldValue(
                        "subcategory_name",
                        selectedSubCategory?.name || "",
                      );
                    }}
                    onBlur={validation.handleBlur}
                    value={validation.values.subcategory_id || 0}
                    dropdownOptions={subcategoryOptions}
                    error={validation.errors.subcategory_name}
                    touched={validation.touched.subcategory_name}
                    onSearch={setSearchSubCategory}
                    isLoadingOptions={isLoadingSubCategory}
                    isDisabled={
                      isEdit ||
                      !validation.values.category_id ||
                      validation.values.category_id === 0
                    }
                  />
                </div>
                {hideCategoryActions ? null : (
                  <div className="mt-3">
                    <CustomButton
                      newClassName="bg-white border-1 border-border align-items-center justify-content-center gap-1"
                      text="Subcategory"
                      textColor="text-black2D"
                      leftIcon={
                        <i
                          className="ri-add-line align-bottom text-black2D"
                          style={{ fontSize: 17 }}
                        />
                      }
                      onClick={onAddSubCategoryClick}
                    />
                  </div>
                )}
              </div>
            </Col>
          </Row>
          <Row>
            <Col lg={12}>
              <CustomInput
                label="Item Name"
                name="name"
                value={validation.values.name || ""}
                onChange={validation.handleChange}
                onBlur={validation.handleBlur}
                error={validation.errors.name}
                touched={validation.touched.name}
                placeholder="Item name"
                type="text"
              />
            </Col>

            <Col lg={12}>
              <CustomInput
                name="unit"
                label="Unit"
                placeholder="Enter unit (e.g., kg, pcs, liters)"
                type="select"
                onChange={(e) => {
                  validation.setFieldValue("unit", e.target.value);
                }}
                onBlur={validation.handleBlur}
                value={validation.values.unit || ""}
                dropdownOptions={configData?.measurement_units.map((unit) => {
                  return {
                    label: unit.description,
                    value: unit.id,
                  };
                })}
                error={validation.errors.unit}
                touched={validation.touched.unit}
              />
            </Col>

            <Col lg={12}>
              <CustomInput
                label="Price per Unit"
                name="price"
                value={validation.values.price || ""}
                onChange={validation.handleChange}
                onBlur={validation.handleBlur}
                error={validation.errors.price}
                touched={validation.touched.price}
                placeholder="e.g. 10"
                type="number"
              />
            </Col>

            <Col lg={12}>
              <CustomInput
                label="Unit Cost"
                name="cost"
                value={validation.values.cost ?? ""}
                onChange={validation.handleChange}
                onBlur={validation.handleBlur}
                error={validation.errors.cost}
                touched={validation.touched.cost}
                placeholder="e.g. 5"
                type="number"
              />
            </Col>
          </Row>
        </ModalBody>
        <ModalFooter className="d-flex justify-content-start">
          <div className="hstack gap-2">
            <CustomButton
              text={isEdit ? "Update Item" : "Save Item"}
              className="bg-primary br-7"
              type="submit"
            />
            {/* <CustomButton
              text="Cancel"
              className="bg-white border-1 border-border br-7"
              textColor="text-black2D"
              onClick={toggle}
              type="button"
            /> */}
          </div>
        </ModalFooter>
      </form>
    </Modal>
  );
};

// Add custom comparison function for React.memo
export default React.memo(ManageItemModal, (prevProps, nextProps) => {
  // Only re-render if these specific props change
  return (
    prevProps.showModal === nextProps.showModal &&
    prevProps.isEdit === nextProps.isEdit &&
    prevProps.selectedItem?.id === nextProps.selectedItem?.id &&
    prevProps.selectedItem?.name === nextProps.selectedItem?.name &&
    prevProps.selectedItem?.price === nextProps.selectedItem?.price &&
    prevProps.selectedItem?.quantity === nextProps.selectedItem?.quantity &&
    prevProps.selectedItem?.unit === nextProps.selectedItem?.unit &&
    prevProps.selectedItem?.category_id ===
      nextProps.selectedItem?.category_id &&
    prevProps.selectedItem?.subcategory_id ===
      nextProps.selectedItem?.subcategory_id
  );
});
